/**
 * Frontend logging utility for FamilyHub.
 * Logs to console in development and can be extended to send to backend.
 */

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: unknown;
  context?: string;
}

class Logger {
  private static instance: Logger;
  private logs: LogEntry[] = [];
  private maxLogs = 1000;
  private isDev = import.meta.env.DEV;

  private constructor() {}

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private formatMessage(
    level: LogLevel,
    context: string,
    message: string,
  ): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${level.toUpperCase()}] [${context}] ${message}`;
  }

  private addLog(entry: LogEntry): void {
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
  }

  private log(
    level: LogLevel,
    context: string,
    message: string,
    data?: unknown,
  ): void {
    const timestamp = new Date().toISOString();
    const entry: LogEntry = { timestamp, level, message, data, context };
    this.addLog(entry);

    if (this.isDev) {
      const formatted = this.formatMessage(level, context, message);
      switch (level) {
        case "debug":
          console.debug(formatted, data || "");
          break;
        case "info":
          console.info(formatted, data || "");
          break;
        case "warn":
          console.warn(formatted, data || "");
          break;
        case "error":
          console.error(formatted, data || "");
          break;
      }
    }
  }

  debug(context: string, message: string, data?: unknown): void {
    this.log("debug", context, message, data);
  }

  info(context: string, message: string, data?: unknown): void {
    this.log("info", context, message, data);
  }

  warn(context: string, message: string, data?: unknown): void {
    this.log("warn", context, message, data);
  }

  error(context: string, message: string, data?: unknown): void {
    this.log("error", context, message, data);
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
  }

  // Export logs to JSON (can be downloaded or sent to backend)
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Convenience functions for different contexts
export const createLogger = (context: string) => ({
  debug: (message: string, data?: unknown) =>
    logger.debug(context, message, data),
  info: (message: string, data?: unknown) =>
    logger.info(context, message, data),
  warn: (message: string, data?: unknown) =>
    logger.warn(context, message, data),
  error: (message: string, data?: unknown) =>
    logger.error(context, message, data),
});

// Example usage:
// const log = createLogger('AuthStore');
// log.info('User logged in', { userId: user.id });
